import React, { useEffect, useMemo, useRef, useState } from "react";

/** 2048 Game (4x4). Arrow/WASD + swipe. Keeps Best Score in localStorage. */
export default function Game2048({ title = "Play 2048", size = 4 }) {
  const N = Math.max(3, Math.min(6, Number(size) || 4)); // allow 3..6 if you ever want
  const emptyGrid = useMemo(() => Array.from({ length: N }, () => Array(N).fill(0)), [N]);

  const [grid, setGrid] = useState(() => spawnRandom(spawnRandom(clone(emptyGrid))));
  const [score, setScore] = useState(0);
  const [best, setBest] = useState(() => Number(localStorage.getItem("g2048_best") || 0));
  const [won, setWon] = useState(false);
  const [over, setOver] = useState(false);
  const touchStart = useRef({ x: 0, y: 0 });

  // Update best score
  useEffect(() => {
    if (score > best) {
      setBest(score);
      localStorage.setItem("g2048_best", String(score));
    }
  }, [score, best]);

  // Keyboard
  useEffect(() => {
    const handle = (e) => {
      const k = e.key.toLowerCase();
      if (k === "arrowup" || k === "w") move("up");
      if (k === "arrowdown" || k === "s") move("down");
      if (k === "arrowleft" || k === "a") move("left");
      if (k === "arrowright" || k === "d") move("right");
      if (k === "r") restart();
    };
    window.addEventListener("keydown", handle);
    return () => window.removeEventListener("keydown", handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grid, score]);

  // Swipe
  function onTouchStart(e) {
    const t = e.touches[0]; touchStart.current = { x: t.clientX, y: t.clientY };
  }
  function onTouchEnd(e) {
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStart.current.x;
    const dy = t.clientY - touchStart.current.y;
    const ax = Math.abs(dx), ay = Math.abs(dy);
    if (Math.max(ax, ay) < 24) return;
    if (ax > ay) move(dx > 0 ? "right" : "left");
    else move(dy > 0 ? "down" : "up");
  }

  function restart() {
    setScore(0);
    setWon(false);
    setOver(false);
    setGrid(spawnRandom(spawnRandom(clone(emptyGrid))));
  }

  function move(dir) {
    if (over) return;
    const { next, gained } = moveGrid(grid, dir);
    if (!changed(grid, next)) return;

    const withSpawn = spawnRandom(next);
    setGrid(withSpawn);
    if (gained) setScore((s) => s + gained);
    if (!won && contains2048(withSpawn)) setWon(true);
    if (!canMove(withSpawn)) setOver(true);
  }

  return (
    <div className="card shadow-sm border-0" style={{ maxWidth: 520 }}>
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h2 className="h5 mb-1 text-dark">{title}</h2>
            <div className="text-muted small">Use Arrow keys / WASD or swipe. Press R to restart.</div>
          </div>
          <div className="d-flex gap-2">
            <Stat label="Score" value={score} />
            <Stat label="Best" value={best} />
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center my-2">
          <button className="btn btn-sm btn-primary" onClick={restart}>New Game</button>
          <div className="text-muted small">
            {won && !over && <span className="text-success me-2">You made 2048! 🎉</span>}
            {over && <span className="text-danger">Game Over</span>}
          </div>
        </div>

        <div
          className="p-2 rounded"
          style={{
            background: "#bbada0",
            width: "100%",
            aspectRatio: "1 / 1",
            touchAction: "none",
            userSelect: "none",
          }}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div
            className="h-100 w-100"
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${N}, 1fr)`,
              gridTemplateRows: `repeat(${N}, 1fr)`,
              gap: 8,
            }}
          >
            {grid.map((row, r) =>
              row.map((v, c) => (
                <Tile key={`${r}-${c}`} value={v} />
              ))
            )}
          </div>
        </div>

        <div className="text-muted small mt-2">
          Tip: combine tiles with the same number to reach <strong>2048</strong>.
        </div>
      </div>
    </div>
  );
}

/* ---------- subcomponents ---------- */

function Stat({ label, value }) {
  return (
    <div className="text-center px-2 py-1 rounded" style={{ background: "#efe6db", minWidth: 76 }}>
      <div className="small text-uppercase text-muted" style={{ letterSpacing: ".06em" }}>{label}</div>
      <div className="fw-bold">{value}</div>
    </div>
  );
}

function Tile({ value }) {
  const { bg, fg } = tileColors(value);
  return (
    <div
      className="d-flex align-items-center justify-content-center rounded"
      style={{
        background: value ? bg : "rgba(238, 228, 218, .35)",
        color: fg,
        fontWeight: 700,
        fontSize: value >= 1024 ? 22 : value >= 128 ? 24 : 26,
        transition: "transform .08s ease",
      }}
    >
      {value ? value : ""}
    </div>
  );
}

/* ---------- game logic ---------- */

function clone(g) { return g.map(r => r.slice()); }

function spawnRandom(g) {
  const empty = [];
  for (let r = 0; r < g.length; r++)
    for (let c = 0; c < g.length; c++)
      if (!g[r][c]) empty.push([r, c]);
  if (!empty.length) return g;
  const [r, c] = empty[Math.floor(Math.random() * empty.length)];
  g[r][c] = Math.random() < 0.9 ? 2 : 4;
  return g;
}

function changed(a, b) {
  for (let r = 0; r < a.length; r++)
    for (let c = 0; c < a.length; c++)
      if (a[r][c] !== b[r][c]) return true;
  return false;
}

function rotateRight(g) {
  const N = g.length;
  const out = Array.from({ length: N }, () => Array(N).fill(0));
  for (let r = 0; r < N; r++)
    for (let c = 0; c < N; c++)
      out[c][N - 1 - r] = g[r][c];
  return out;
}

function rotateLeft(g) {
  const N = g.length;
  const out = Array.from({ length: N }, () => Array(N).fill(0));
  for (let r = 0; r < N; r++)
    for (let c = 0; c < N; c++)
      out[N - 1 - c][r] = g[r][c];
  return out;
}

function flip(g) { // horizontal flip
  return g.map(row => row.slice().reverse());
}

/** compress + merge a row to the left; return {row, gained} */
function mergeRowLeft(row) {
  const n = row.filter(v => v !== 0);
  let gained = 0;
  for (let i = 0; i < n.length - 1; i++) {
    if (n[i] !== 0 && n[i] === n[i + 1]) {
      n[i] *= 2;
      gained += n[i];
      n[i + 1] = 0;
      i++;
    }
  }
  const merged = n.filter(v => v !== 0);
  while (merged.length < row.length) merged.push(0);
  return { row: merged, gained };
}

function moveGrid(g, dir) {
  let mat = clone(g);
  let gainedTotal = 0;

  if (dir === "up") {
    mat = rotateLeft(mat);
  } else if (dir === "down") {
    mat = rotateRight(mat);
  } else if (dir === "right") {
    mat = flip(mat);
  }

  for (let r = 0; r < mat.length; r++) {
    const { row, gained } = mergeRowLeft(mat[r]);
    mat[r] = row;
    gainedTotal += gained;
  }

  if (dir === "up") {
    mat = rotateRight(mat);
  } else if (dir === "down") {
    mat = rotateLeft(mat);
  } else if (dir === "right") {
    mat = flip(mat);
  }

  return { next: mat, gained: gainedTotal };
}

function canMove(g) {
  // any empty?
  for (let r = 0; r < g.length; r++)
    for (let c = 0; c < g.length; c++)
      if (g[r][c] === 0) return true;

  // any mergeable neighbors?
  const N = g.length;
  for (let r = 0; r < N; r++) {
    for (let c = 0; c < N; c++) {
      if (r + 1 < N && g[r][c] === g[r + 1][c]) return true;
      if (c + 1 < N && g[r][c] === g[r][c + 1]) return true;
    }
  }
  return false;
}

function contains2048(g) {
  for (let r = 0; r < g.length; r++)
    for (let c = 0; c < g.length; c++)
      if (g[r][c] >= 2048) return true;
  return false;
}

function tileColors(v) {
  const palette = {
    0:  { bg: "rgba(238, 228, 218, .35)", fg: "#776e65" },
    2:  { bg: "#eee4da", fg: "#776e65" },
    4:  { bg: "#ede0c8", fg: "#776e65" },
    8:  { bg: "#f2b179", fg: "white" },
    16: { bg: "#f59563", fg: "white" },
    32: { bg: "#f67c5f", fg: "white" },
    64: { bg: "#f65e3b", fg: "white" },
    128:{ bg: "#edcf72", fg: "white" },
    256:{ bg: "#edcc61", fg: "white" },
    512:{ bg: "#edc850", fg: "white" },
    1024:{bg: "#edc53f", fg: "white" },
    2048:{bg: "#edc22e", fg: "white" },
  };
  return palette[v] || { bg: "#3c3a32", fg: "white" };
}